/**
 * <video-facade> — deferred Wistia embed with a blur-up poster (FND-802).
 *
 * Paints a blurred low-res image immediately, fades the full image in over it once
 * decoded, then injects the Wistia iframe after the page has loaded and fades it in
 * on top. Keeps E-v1.js and the embed off the critical path so nothing flashes white
 * while the video is still coming down.
 */
class VideoFacade extends HTMLElement {
  connectedCallback() {
    this.sharpen();

    if (!('IntersectionObserver' in window)) {
      this.queueVideo();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.observer.disconnect();
          this.queueVideo();
        }
      },
      { rootMargin: '200px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  /* Swap the blurred placeholder for the full image as soon as it has decoded. */
  sharpen() {
    const full = this.querySelector('.usamm_video__poster--full');
    if (!full) return;

    if (full.complete) {
      this.classList.add('is-sharp');
    } else {
      full.addEventListener('load', () => this.classList.add('is-sharp'), { once: true });
      full.addEventListener('error', () => this.classList.add('is-sharp'), { once: true });
    }
  }

  /* Hold the embed until the page has finished loading, so it never competes with paint. */
  queueVideo() {
    if (this.videoQueued) return;
    this.videoQueued = true;

    if (document.readyState === 'complete') {
      this.loadVideo();
    } else {
      window.addEventListener('load', () => this.loadVideo(), { once: true });
    }
  }

  loadVideo() {
    const url = this.dataset.videoUrl;
    if (!url) return;

    const iframe = document.createElement('iframe');
    iframe.src = url;
    iframe.title = this.dataset.videoTitle || '';
    iframe.className = 'wistia_embed';
    iframe.name = 'wistia_embed';
    iframe.width = '100%';
    iframe.height = '100%';
    iframe.allow = 'autoplay; fullscreen';
    iframe.allowFullscreen = true;
    iframe.setAttribute('allowtransparency', 'true');
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('scrolling', 'no');
    iframe.setAttribute('msallowfullscreen', '');
    iframe.addEventListener('load', () => this.classList.add('is-video-ready'), { once: true });

    (this.querySelector('.wistia_responsive_wrapper') || this).appendChild(iframe);

    if (!document.querySelector('script[src*="fast.wistia.net/assets/external/E-v1.js"]')) {
      const script = document.createElement('script');
      script.src = 'https://fast.wistia.net/assets/external/E-v1.js';
      script.async = true;
      document.head.appendChild(script);
    }
  }
}

customElements.define('video-facade', VideoFacade);
